import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  type TableOptions,
  type ColumnDef,
  type PaginationState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";

export interface UseRemoteTableOptions<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  pagination?: PaginationState;
  sorting?: SortingState;
  columnVisibility?: VisibilityState;
  pageCount?: number;
  onPaginationChange?: TableOptions<TData>["onPaginationChange"];
  onSortingChange?: TableOptions<TData>["onSortingChange"];
  onColumnVisibilityChange?: TableOptions<TData>["onColumnVisibilityChange"];
  enableColumnResizing?: boolean;
  defaultColumn?: TableOptions<TData>["defaultColumn"];

  //   If true - uses client-side sorting (getSortedRowModel).
  //   If false - uses manual sorting (server-side).
  //   Only applies when sorting is provided.
  clientSideSorting?: boolean;
}

export function useRemoteTable<TData>({
  data,
  columns,
  pagination,
  sorting,
  columnVisibility,
  pageCount,
  onPaginationChange,
  onSortingChange,
  onColumnVisibilityChange,
  enableColumnResizing = false,
  defaultColumn,
  clientSideSorting = false,
}: UseRemoteTableOptions<TData>) {
  const table = useReactTable<TData>({
    data,
    columns,
    pageCount,
    defaultColumn,
    state: {
      ...(pagination && { pagination }),
      ...(sorting && { sorting }),
      ...(columnVisibility && { columnVisibility }),
    },
    ...(onPaginationChange && { onPaginationChange }),
    ...(onSortingChange && { onSortingChange }),
    ...(onColumnVisibilityChange && { onColumnVisibilityChange }),
    getCoreRowModel: getCoreRowModel(),
    ...(sorting &&
      clientSideSorting && { getSortedRowModel: getSortedRowModel() }),
    manualPagination: !!pagination,
    manualSorting: !!sorting && !clientSideSorting,
    enableSorting: !!sorting,
    enableColumnResizing,
    columnResizeMode: enableColumnResizing ? "onChange" : undefined,
  });

  return table;
}
